import { useEffect, useRef, useState } from "react";
import { COSMETICS, mergeEvents, ownedCosmetics, shellBalance, uuid, type Event, type Session } from "../shared/game";
export type Account = { id: string; username: string };
const STATIC_MODE = __ISLAND_STATIC__;
const FIXED_USERNAME = "13107640679";
const FIXED_PASSWORD = "890709";
type Saved = { events: Event[]; pending: string[]; session: Session | null };
const empty = (): Saved => ({ events: [], pending: [], session: null });
let lastEventAt = 0;
function read<T>(key: string, fallback: T): T {
  try {
    return JSON.parse(localStorage.getItem(key) || "null") ?? fallback;
  } catch {
    return fallback;
  }
}
export async function api(url: string, body?: unknown) {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), 10000);
  let res: Response;
  try {
    res = await fetch(url, {
      method: body === undefined ? "GET" : "POST",
      headers: body === undefined ? {} : { "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: controller.signal,
    });
  } finally {
    window.clearTimeout(timer);
  }
  const text = await res.text();
  let data: any = {};
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = {};
    }
  }
  if (!res.ok) throw new Error(data.error || "连接暂时中断，请稍后重试。");
  return data;
}
export function useIsland() {
  const [account, setAccount] = useState<Account | null>(() =>
    read("island-account", null),
  );
  const key = `island-v1-${account?.id || "guest"}`;
  const [saved, setSaved] = useState<Saved>(() => read(key, empty()));
  const ref = useRef(saved);
  const owner = useRef(key);
  ref.current = saved;
  owner.current = key;
  const [online, setOnline] = useState(navigator.onLine);
  const [message, setMessage] = useState("");
  const [syncing, setSyncing] = useState(false);
  const busy = useRef(false);
  useEffect(() => {
    const change = () => setOnline(navigator.onLine);
    window.addEventListener("online", change);
    window.addEventListener("offline", change);
    return () => {
      window.removeEventListener("online", change);
      window.removeEventListener("offline", change);
    };
  }, []);
  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(saved));
      setMessage((m) => (m.startsWith("本机空间") ? "" : m));
    } catch {
      setMessage("本机空间不足，请保持联网同步，并在家长页导出备份。");
    }
  }, [saved, key]);
  const add = (type: Event["type"], data: Record<string, unknown>, eventId?: string) => {
    lastEventAt = Math.max(Date.now(), lastEventAt + 1);
    const event: Event = {
      id: eventId || uuid(),
      at: new Date(lastEventAt).toISOString(),
      type,
      data,
    };
    setSaved((s) => ({
      ...s,
      events: mergeEvents(s.events, [event]),
      pending: STATIC_MODE ? s.pending : [...s.pending, event.id],
    }));
    return event;
  };
  const awardDailyShells = (date: string) => {
    if (ref.current.events.some((e) => e.id === `shell:daily:${date}`)) return false;
    add("shell", { action: "earn", amount: 10, key: date }, `shell:daily:${date}`);
    return true;
  };
  const purchaseCosmetic = (id: string) => {
    const item = COSMETICS.find((candidate) => candidate.id === id);
    if (!item) return "这个装饰暂时没有上架。";
    if (ownedCosmetics(ref.current.events).has(id)) return "这个装饰已经收藏啦。";
    if (shellBalance(ref.current.events) < item.price) return "贝壳还不够，完成一次冒险就能再攒一些。";
    add("shell", { action: "spend", amount: item.price, key: item.id }, `shell:spend:${id}`);
    return "已放进装饰收藏册。";
  };
  async function sync() {
    if (STATIC_MODE || !account || !navigator.onLine || busy.current) return;
    const currentKey = key;
    busy.current = true;
    setSyncing(true);
    try {
      const ids = ref.current.pending.slice(0, 50);
      const response = await api("/api/sync", {
        familyId: account.id,
        events: ref.current.events.filter((e) => ids.includes(e.id)),
      });
      if (owner.current === currentKey) {
        setSaved((s) => {
          const events = mergeEvents(s.events, response.events);
          const last = events.filter((e) => e.type === "checkpoint").at(-1);
          return {
            ...s,
            events,
            pending: s.pending.filter((id) => !response.accepted.includes(id)),
            session: last ? (last.data.session as Session | null) : s.session,
          };
        });
        setMessage("");
      }
    } catch (error) {
      if (owner.current === currentKey) setMessage((error as Error).message);
    } finally {
      busy.current = false;
      setSyncing(false);
    }
  }
  useEffect(() => {
    if (account && online) {
      const timer = setTimeout(sync, 700);
      return () => clearTimeout(timer);
    }
  }, [account?.id, online, saved.pending.length]);
  useEffect(() => {
    const timer = setInterval(sync, 20000);
    const visible = () => {
      if (!document.hidden) void sync();
    };
    document.addEventListener("visibilitychange", visible);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", visible);
    };
  }, [account?.id]);
  async function auth(
    action: "login" | "register",
    username: string,
    password: string,
    importGuest: boolean,
  ) {
    let result: { id: string; username: string; events: Event[] };
    const normalizedUsername = username.replace(/\s+/g, "");
    try {
      // Sites 的静态预览没有 API；先完成固定家庭登录，避免旧版 Safari
      // 在请求相对地址时抛出 “The string did not match the expected pattern”。
      if (STATIC_MODE && action === "login" &&
          normalizedUsername === FIXED_USERNAME && password === FIXED_PASSWORD) {
        result = { id: "little-island-family", username: FIXED_USERNAME, events: [] };
      } else {
        result = await api(`/api/auth/${action}`, {
          username: normalizedUsername,
          password,
        });
      }
    } catch (error) {
      if (
        !STATIC_MODE ||
        action !== "login" ||
        normalizedUsername !== FIXED_USERNAME ||
        password !== FIXED_PASSWORD
      )
        throw error;
      result = {
        id: "little-island-family",
        username: FIXED_USERNAME,
        events: [],
      };
    }
    const next: Account = { id: result.id, username: result.username };
    const target = read<Saved>(`island-v1-${next.id}`, empty());
    const guest = !account && importGuest ? ref.current : empty();
    const events = mergeEvents(target.events, result.events, guest.events);
    const checkpoint = events.filter((e) => e.type === "checkpoint").at(-1);
    const merged = {
      events,
      pending: [
        ...new Set([...target.pending, ...guest.events.map((e) => e.id)]),
      ],
      session: checkpoint
        ? (checkpoint.data.session as Session | null)
        : target.session || guest.session,
    };
    localStorage.setItem("island-account", JSON.stringify(next));
    setAccount(next);
    setSaved(merged);
    setMessage("");
  }
  async function logout() {
    if (!STATIC_MODE) await api("/api/logout", {});
    localStorage.removeItem("island-account");
    setAccount(null);
    setSaved(read("island-v1-guest", empty()));
    setMessage("");
  }
  return {
    events: saved.events,
    session: saved.session,
    setSession: (session: Session | null) => {
      add("checkpoint", { session });
      setSaved((s) => ({ ...s, session }));
    },
    add,
    awardDailyShells,
    purchaseCosmetic,
    shellBalance: shellBalance(saved.events),
    account,
    auth,
    logout,
    online,
    message,
    syncing,
    pending: saved.pending.length,
    sync,
    exportData: () =>
      JSON.stringify({ version: 1, events: saved.events }, null, 2),
  };
}
export type IslandStore = ReturnType<typeof useIsland>;
